import React from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';
import { AutocompleteProps } from '@mui/material/Autocomplete';
import { TextFieldProps } from '@mui/material/TextField';
import { useAutocomplete } from '@refinedev/mui';
import { Control, Controller, FieldValues, RegisterOptions } from 'react-hook-form';

interface SelectorProps<T extends FieldValues = FieldValues>
  extends Omit<AutocompleteProps<unknown, false, false, false>, 'renderInput' | 'options'> {
  control: Control<T>;
  name: string;
  rules?: RegisterOptions;
  label: string;
  resource: string;
  inputProps?: TextFieldProps;
}

const Selector: React.FC<SelectorProps> = ({ label, resource, inputProps, ...props }) => {
  const { autocompleteProps } = useAutocomplete({
    resource: resource,
    pagination: {
      pageSize: 10000
    },
    onSearch: (query: string) => {
      return [
        {
          field: 'name',
          operator: 'eq',
          value: query
        }
      ];
    },
    sorters: [
      {
        field: 'name',
        order: 'asc'
      }
    ]
  });

  return (
    <Controller
      {...props}
      defaultValue={null}
      render={({ field }) => (
        <Autocomplete
          {...autocompleteProps}
          {...field}
          onChange={(_, value) => {
            field.onChange(value?.id || null);
          }}
          getOptionLabel={item => {
            return item.name
              ? item.name
              : autocompleteProps?.options.find(p => p.id.toString() === item.toString())?.name ?? '';
          }}
          renderOption={(props, option) => {
            return (
              <Box {...props} component="li" key={option.id}>
                {option.name}
              </Box>
            );
          }}
          isOptionEqualToValue={(option, value) => {
            return value === undefined || option?.id?.toString() === value?.toString();
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              margin="normal"
              variant="outlined"
              required={!!props.rules?.required}
              {...inputProps}
            />
          )}
        />
      )}
    />
  );
};

export default Selector;
